import React, { useState, useEffect } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Megaphone, RefreshCw, CheckCircle, Link2, Unlink } from 'lucide-react';

const PROVIDERS = [
  { key: 'klaviyo', name: 'Klaviyo', placeholder: 'pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', help: 'Settings → API Keys → Private API Keys' },
  { key: 'brevo', name: 'Brevo', placeholder: 'xkeysib-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', help: 'Settings → SMTP & API → API Keys' },
];

const ProviderCard = ({ provider, state, onConnected, onDisconnected }) => {
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const connect = async () => {
    if (!apiKey.trim()) {
      toast.error('Introduce la API key');
      return;
    }
    setConnecting(true);
    try {
      const r = await api.post(`/api/popups/integrations/${provider.key}/connect`, { api_key: apiKey.trim() });
      toast.success(r.data.message);
      setApiKey('');
      onConnected(provider.key, r.data.lists || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error al conectar ${provider.name}`);
    } finally {
      setConnecting(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/api/popups/integrations/${provider.key}/refresh-lists`);
      onConnected(provider.key, r.data.lists || []);
      toast.success('Listas actualizadas');
    } catch (e) {
      toast.error('No se pudieron actualizar las listas');
    } finally {
      setRefreshing(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete(`/api/popups/integrations/${provider.key}`);
      onDisconnected(provider.key);
      toast.success(`${provider.name} desconectado`);
    } catch (e) {
      toast.error('Error al desconectar');
    }
  };

  const connected = state?.connected;

  return (
    <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5" data-testid={`marketing-provider-card-${provider.key}`}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-white font-medium text-sm flex items-center gap-2">
          {provider.name}
          {connected && <CheckCircle size={13} className="text-green-400" />}
        </h4>
        {connected && (
          <button
            onClick={disconnect}
            data-testid={`disconnect-${provider.key}-btn`}
            className="text-gray-500 hover:text-red-400 text-xs flex items-center gap-1"
          >
            <Unlink size={12} /> Desconectar
          </button>
        )}
      </div>

      {connected ? (
        <div className="mt-3 space-y-2">
          <p className="text-gray-500 text-xs">
            API key: <span className="text-gray-300">{state.api_key_masked}</span>
          </p>
          <p className="text-gray-500 text-xs">
            {(state.lists || []).length} lista(s) disponible(s) para usar en tus popups.
          </p>
          <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing}
            data-testid={`refresh-lists-${provider.key}-btn`}
            className="text-xs gap-1 bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800">
            {refreshing ? <RefreshCw size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Actualizar listas
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder={provider.placeholder}
            data-testid={`${provider.key}-api-key-input`}
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
          <Button size="sm" onClick={connect} disabled={connecting}
            data-testid={`connect-${provider.key}-btn`}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1 whitespace-nowrap">
            {connecting ? <RefreshCw size={11} className="animate-spin" /> : <Link2 size={11} />}
            Conectar
          </Button>
        </div>
      )}
      {!connected && <p className="text-gray-600 text-[11px] mt-1.5">Obtén tu API key en: {provider.help}</p>}
    </div>
  );
};

export const MarketingIntegrationsSection = () => {
  const { organization } = useOrganization();
  const [integrations, setIntegrations] = useState({ klaviyo: {}, brevo: {} });
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/api/popups/integrations')
      .then(r => setIntegrations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!organization?.id) return;
    load();
  }, [organization?.id]);

  const handleConnected = (key, lists) => {
    setIntegrations(prev => ({ ...prev, [key]: { connected: true, lists, api_key_masked: prev[key]?.api_key_masked || '••••••' } }));
    load();
  };
  const handleDisconnected = (key) => {
    setIntegrations(prev => ({ ...prev, [key]: { connected: false, lists: [] } }));
  };

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex items-center justify-center">
        <RefreshCw size={18} className="animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6" data-testid="marketing-integrations-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
          <Megaphone size={18} className="text-indigo-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Integraciones de marketing</h3>
          <p className="text-gray-400 text-sm mt-0.5">
            Conecta Klaviyo o Brevo para enviar automáticamente los leads capturados por tus Popups a una lista.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {PROVIDERS.map(p => (
          <ProviderCard
            key={p.key}
            provider={p}
            state={integrations[p.key]}
            onConnected={handleConnected}
            onDisconnected={handleDisconnected}
          />
        ))}
      </div>
    </div>
  );
};
